import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import QMSLayout from "../components/QMSLayout";
import { CheckCircle2, Circle, AlertTriangle, ChevronDown, ChevronRight, FileText, Shield, Layers, Users, ClipboardCheck, BarChart3, ArrowLeft, ThumbsUp, ThumbsDown, RefreshCw, MessageSquare, Send, Clock, XCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  quality_management: { label: "Quality Management", icon: Shield, color: "#36A9E1" },
  risk_management: { label: "Risk Management", icon: AlertTriangle, color: "#FBBF24" },
  regulatory: { label: "Regulatory", icon: FileText, color: "#F87171" },
  supplier_quality: { label: "Supplier Quality", icon: Users, color: "#4ADE80" },
  corrective_actions: { label: "CAPA", icon: ClipboardCheck, color: "#A78BFA" },
  audits: { label: "Audits", icon: BarChart3, color: "#38BDF8" },
  training: { label: "Training", icon: Layers, color: "#FB923C" },
  software_lifecycle: { label: "Software Lifecycle", icon: FileText, color: "#36A9E1" },
  cybersecurity: { label: "Cybersecurity", icon: Shield, color: "#F87171" },
  data_integrity: { label: "Data Integrity", icon: Shield, color: "#FBBF24" },
  usability: { label: "Usability", icon: Users, color: "#4ADE80" },
  general: { label: "General", icon: ClipboardCheck, color: "#8B95A5" }
};

const STATUS_BADGE = {
  draft: { bg: "bg-slate-400/10", text: "text-slate-400", label: "Draft" },
  submitted: { bg: "bg-blue-400/10", text: "text-blue-400", label: "Submitted" },
  needs_revision: { bg: "bg-amber-400/10", text: "text-amber-400", label: "Needs Revision" },
  accepted: { bg: "bg-emerald-400/10", text: "text-emerald-400", label: "Accepted" },
  rejected: { bg: "bg-red-400/10", text: "text-red-400", label: "Rejected" },
  no_response: { bg: "bg-slate-500/10", text: "text-slate-500", label: "No Response" }
};

export default function ClientOnboardingReviewPage() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("operations");
  const [runDetail, setRunDetail] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCats, setExpandedCats] = useState({});
  const [activeItem, setActiveItem] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [runRes, sumRes] = await Promise.all([
        fetch(`${API}/api/client-onboarding/runs/${runId}`, { credentials: "include" }),
        fetch(`${API}/api/client-onboarding/runs/${runId}/summary`, { credentials: "include" })
      ]);
      const runData = await runRes.json();
      const sumData = await sumRes.json();
      setRunDetail(runData);
      setSummary(sumData);
      const cats = {};
      (runData.items || []).forEach(i => { cats[i.category] = true; });
      setExpandedCats(cats);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [runId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const submitReview = async (itemId, decision, comment) => {
    await fetch(`${API}/api/client-onboarding/runs/${runId}/review`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ item_id: itemId, decision, comment })
    });
    fetchData();
  };

  const addComment = async (itemId, comment) => {
    await fetch(`${API}/api/client-onboarding/runs/${runId}/comments`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ item_id: itemId, comment })
    });
    fetchData();
  };

  const toggleCat = (cat) => setExpandedCats(prev => ({ ...prev, [cat]: !prev[cat] }));

  const groupItems = (items) => {
    const groups = {};
    (items || []).forEach(item => {
      const cat = item.category || "general";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(item);
    });
    return groups;
  };

  const getItemStatus = (itemId) => {
    if (!runDetail?.responses) return "no_response";
    const resp = runDetail.responses[itemId];
    return resp ? resp.status : "no_response";
  };

  const getItemResponse = (itemId) => runDetail?.responses?.[itemId] || null;
  const getItemComments = (itemId) => (runDetail?.comments || []).filter(c => c.item_id === itemId);
  const getItemArtifacts = (itemId) => (runDetail?.artifacts || []).filter(a => a.item_id === itemId);
  const getItemReviews = (itemId) => (runDetail?.review_logs || []).filter(r => r.item_id === itemId);

  if (loading) return <QMSLayout title="Review Onboarding"><div className="text-center py-12 text-[#8B95A5]">Loading...</div></QMSLayout>;
  if (!runDetail) return <QMSLayout title="Review Onboarding"><div className="text-center py-12 text-[#F87171]">Run not found</div></QMSLayout>;

  return (
    <QMSLayout title={`Review: ${runDetail.client_name}`}>
      <div className="space-y-5" data-testid="review-page">
        <div className="flex items-center justify-between">
          <div>
            <button onClick={() => navigate("/onboarding-checklist")} className="text-sm text-[#8B95A5] hover:text-white mb-2 flex items-center gap-1" data-testid="back-to-checklist">
              <ArrowLeft size={14} /> Back to Onboarding
            </button>
            <p className="text-[#8B95A5] text-sm">{runDetail.template_name} · {runId}</p>
          </div>
          {summary && (
            <div className="flex items-center gap-6">
              <div className="text-right">
                <p className="text-2xl font-bold text-white">{summary.readiness_pct}%</p>
                <p className="text-xs text-[#8B95A5]">{summary.accepted_items}/{summary.total_items} accepted</p>
              </div>
              <div className={`px-4 py-2 rounded-lg text-sm font-semibold ${summary.readiness_pct === 100 ? "bg-[#4ADE80]/10 text-[#4ADE80]" : "bg-[#FBBF24]/10 text-[#FBBF24]"}`}>
                {summary.readiness_pct === 100 ? "READY" : "IN REVIEW"}
              </div>
            </div>
          )}
        </div>

        {/* Summary bar */}
        {summary && (
          <div className="qms-card">
            <div className="qms-card-body">
              <div className="flex flex-wrap gap-4 text-sm mb-3">
                <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-emerald-400" /> Accepted: {summary.accepted_items}</span>
                <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-blue-400" /> Submitted: {summary.submitted_items}</span>
                <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-amber-400" /> Needs Revision: {summary.needs_revision_items}</span>
                <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-slate-400" /> Draft: {summary.draft_items}</span>
                <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-slate-600" /> No Response: {summary.no_response_items}</span>
              </div>
              <div className="w-full h-3 bg-[#0D0F14] rounded-full overflow-hidden flex">
                {summary.total_items > 0 && (
                  <>
                    <div className="h-full bg-emerald-400" style={{ width: `${(summary.accepted_items / summary.total_items) * 100}%` }} />
                    <div className="h-full bg-blue-400" style={{ width: `${(summary.submitted_items / summary.total_items) * 100}%` }} />
                    <div className="h-full bg-amber-400" style={{ width: `${(summary.needs_revision_items / summary.total_items) * 100}%` }} />
                    <div className="h-full bg-slate-400" style={{ width: `${(summary.draft_items / summary.total_items) * 100}%` }} />
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Items by category */}
        {Object.entries(groupItems(runDetail.items)).map(([cat, items]) => {
          const meta = CATEGORY_META[cat] || CATEGORY_META.general;
          const Icon = meta.icon;
          const accepted = items.filter(i => getItemStatus(i.item_id) === "accepted").length;
          const expanded = expandedCats[cat] !== false;

          return (
            <div key={cat} className="qms-card" data-testid={`review-category-${cat}`}>
              <div className="qms-card-body">
                <button onClick={() => toggleCat(cat)} className="w-full flex items-center justify-between py-1">
                  <div className="flex items-center gap-3">
                    <Icon size={18} style={{ color: meta.color }} />
                    <span className="text-white font-semibold">{meta.label}</span>
                    <span className="text-xs text-[#8B95A5]">({accepted}/{items.length} accepted)</span>
                  </div>
                  {expanded ? <ChevronDown size={16} className="text-[#8B95A5]" /> : <ChevronRight size={16} className="text-[#8B95A5]" />}
                </button>

                {expanded && (
                  <div className="mt-3 space-y-2">
                    {items.sort((a, b) => a.order - b.order).map(item => {
                      const status = getItemStatus(item.item_id);
                      const resp = getItemResponse(item.item_id);
                      const comments = getItemComments(item.item_id);
                      const artifacts = getItemArtifacts(item.item_id);
                      const reviews = getItemReviews(item.item_id);
                      const badge = STATUS_BADGE[status] || STATUS_BADGE.no_response;
                      const isActive = activeItem === item.item_id;

                      return (
                        <div key={item.item_id} className={`p-3 rounded border transition-colors ${status === "accepted" ? "border-emerald-500/20 bg-emerald-500/5" : status === "submitted" ? "border-blue-500/20 bg-blue-500/5" : status === "needs_revision" ? "border-amber-500/20 bg-amber-500/5" : "border-[rgba(255,255,255,0.05)]"}`} data-testid={`review-item-${item.item_id}`}>
                          <div className="flex items-start justify-between gap-3 cursor-pointer" onClick={() => setActiveItem(isActive ? null : item.item_id)}>
                            <div className="flex items-start gap-3 flex-1">
                              {status === "accepted" ? <CheckCircle2 size={20} className="text-[#4ADE80] mt-0.5 shrink-0" /> :
                               status === "submitted" ? <Clock size={20} className="text-[#38BDF8] mt-0.5 shrink-0" /> :
                               status === "needs_revision" ? <RefreshCw size={20} className="text-[#FBBF24] mt-0.5 shrink-0" /> :
                               <Circle size={20} className="text-[#8B95A5] mt-0.5 shrink-0" />}
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium text-sm text-white">{item.title}</span>
                                  {item.required && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F87171]/10 text-[#F87171]">Required</span>}
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${badge.bg} ${badge.text}`}>{badge.label}</span>
                                </div>
                                {item.standard_refs?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {item.standard_refs.map(ref => (
                                      <span key={ref} className="text-[10px] px-1.5 py-0.5 rounded bg-[#36A9E1]/10 text-[#36A9E1] font-mono">{ref}</span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                            {isActive ? <ChevronDown size={16} className="text-[#8B95A5]" /> : <ChevronRight size={16} className="text-[#8B95A5]" />}
                          </div>

                          {isActive && (
                            <ReviewItemPanel
                              item={item}
                              response={resp}
                              comments={comments}
                              artifacts={artifacts}
                              reviews={reviews}
                              status={status}
                              onReview={(decision, comment) => submitReview(item.item_id, decision, comment)}
                              onComment={(text) => addComment(item.item_id, text)}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </QMSLayout>
  );
}

function ReviewItemPanel({ item, response, comments, artifacts, reviews, status, onReview, onComment }) {
  const [reviewComment, setReviewComment] = useState("");
  const [commentText, setCommentText] = useState("");

  const handleDecision = async (decision) => {
    await onReview(decision, reviewComment);
    setReviewComment("");
  };

  return (
    <div className="mt-3 pt-3 border-t border-[rgba(255,255,255,0.05)] space-y-3" data-testid={`review-detail-${item.item_id}`}>
      {/* Client response */}
      {response ? (
        <div className="p-3 rounded bg-[#0D0F14] border-l-2 border-[#36A9E1]">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-medium text-[#36A9E1]">Client Response</span>
            <span className="text-xs text-[#555]">{response.client_user_name}</span>
          </div>
          <p className="text-sm text-[#CBD5E1]">{response.response_text}</p>
        </div>
      ) : (
        <p className="text-xs text-[#8B95A5] italic">No response submitted yet</p>
      )}

      {/* Artifacts */}
      {artifacts.length > 0 && (
        <div>
          <label className="text-xs text-[#8B95A5] block mb-1">Artifacts ({artifacts.length})</label>
          {artifacts.map(a => (
            <div key={a.artifact_id} className="flex items-center gap-2 text-xs p-2 rounded bg-[#0D0F14] mb-1">
              <FileText size={14} className="text-[#36A9E1]" />
              <span className="text-white">{a.file_name}</span>
              <span className="text-[#555]">{a.artifact_type}</span>
            </div>
          ))}
        </div>
      )}

      {/* Previous reviews */}
      {reviews.length > 0 && (
        <div>
          <label className="text-xs text-[#8B95A5] block mb-1">Review History</label>
          {reviews.slice(0, 5).map(r => (
            <div key={r.review_log_id} className="flex items-start gap-2 text-xs p-2 rounded bg-[#0D0F14] mb-1">
              {r.decision === "accepted" ? <ThumbsUp size={14} className="text-[#4ADE80] shrink-0 mt-0.5" /> :
               r.decision === "rejected" ? <ThumbsDown size={14} className="text-[#F87171] shrink-0 mt-0.5" /> :
               <RefreshCw size={14} className="text-[#FBBF24] shrink-0 mt-0.5" />}
              <div>
                <span className="text-white font-medium">{r.reviewer_name}</span>
                <span className="text-[#555] ml-2">{r.decision.replace("_", " ")}</span>
                {r.comment && <p className="text-[#CBD5E1] mt-0.5">{r.comment}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Comments */}
      {comments.length > 0 && (
        <div>
          <label className="text-xs text-[#8B95A5] block mb-1">Comments</label>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {comments.map(c => (
              <div key={c.comment_id} className={`text-xs p-2 rounded ${c.actor_type === "client" ? "bg-[#36A9E1]/5 border-l-2 border-[#36A9E1]" : "bg-[#FBBF24]/5 border-l-2 border-[#FBBF24]"}`}>
                <span className="font-medium text-white">{c.actor_name}</span>
                <span className="text-[#555] ml-2">{c.actor_type}</span>
                <p className="text-[#CBD5E1] mt-0.5">{c.comment}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Review actions */}
      {status !== "accepted" && (
        <div className="space-y-2 p-3 rounded bg-[#1F222D] border border-[rgba(255,255,255,0.05)]">
          <label className="text-xs text-[#8B95A5] block">Review Decision</label>
          <textarea
            value={reviewComment}
            onChange={e => setReviewComment(e.target.value)}
            placeholder="Review comment (optional)..."
            rows={2}
            className="w-full bg-[#0D0F14] border border-[rgba(255,255,255,0.06)] rounded px-2 py-1.5 text-xs text-white placeholder-[#555] resize-none outline-none focus:border-[#36A9E1]/40"
            data-testid={`review-comment-${item.item_id}`}
          />
          <div className="flex gap-2">
            <button onClick={() => handleDecision("accepted")} disabled={!response} className="text-xs px-3 py-1.5 rounded bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 flex items-center gap-1 disabled:opacity-40" data-testid={`accept-${item.item_id}`}>
              <ThumbsUp size={12} /> Accept
            </button>
            <button onClick={() => handleDecision("needs_revision")} disabled={!response} className="text-xs px-3 py-1.5 rounded bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 flex items-center gap-1 disabled:opacity-40" data-testid={`revision-${item.item_id}`}>
              <RefreshCw size={12} /> Request Revision
            </button>
            <button onClick={() => handleDecision("rejected")} disabled={!response} className="text-xs px-3 py-1.5 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30 flex items-center gap-1 disabled:opacity-40" data-testid={`reject-${item.item_id}`}>
              <ThumbsDown size={12} /> Reject
            </button>
          </div>
        </div>
      )}

      {/* Add comment */}
      <div className="flex gap-2">
        <input
          value={commentText}
          onChange={e => setCommentText(e.target.value)}
          placeholder="Add internal comment..."
          className="flex-1 bg-[#0D0F14] border border-[rgba(255,255,255,0.06)] rounded px-2 py-1.5 text-xs text-white placeholder-[#555] outline-none"
          onKeyDown={e => { if (e.key === "Enter" && commentText.trim()) { onComment(commentText); setCommentText(""); } }}
          data-testid={`internal-comment-${item.item_id}`}
        />
        <button onClick={() => { if (commentText.trim()) { onComment(commentText); setCommentText(""); } }} className="text-xs px-2 py-1.5 rounded bg-[#1F222D] text-[#8B95A5] hover:text-white">
          <Send size={14} />
        </button>
      </div>
    </div>
  );
}
